import React, { useState, useEffect } from 'react';
import assetUserService from '../services/assetUserService';
import { FaClock, FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';
import '../styles/ExtensionReleaseRequests.css';
import AssistantSidebar from './AssistantSidebar';

const ExtensionReleaseRequests = ({ user, onClose }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedReject, setSelectedReject] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await assetUserService.getOtherRequests();
      if (response.success || response.requests) {
        setRequests(response.requests || []);
      }
    } catch (err) {
      setError('Failed to load requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setApproving(requestId);
      const result = await assetUserService.approveAssetRequest(requestId, {
        remarks: 'Approved by asset incharge'
      });
      if (result.success) {
        alert('Request approved successfully');
        fetchRequests();
      }
    } catch (err) {
      alert('Error approving request: ' + err.message);
    } finally {
      setApproving(null);
    }
  };

  const handleRejectClick = (request) => {
    setSelectedReject(request);
    setRejectReason('');
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    try {
      setRejecting(selectedReject._id);
      const result = await assetUserService.rejectAssetRequest(selectedReject._id, {
        rejectionReason: rejectReason
      });
      if (result.success) {
        alert('Request rejected successfully');
        setSelectedReject(null);
        setRejectReason('');
        fetchRequests();
      }
    } catch (err) {
      alert('Error rejecting request: ' + err.message);
    } finally {
      setRejecting(null);
    }
  };

  const getRequestTypeLabel = (type) => {
    if (type === 'asset_extension') return '⏱️ Extension Request';
    if (type === 'asset_release') return '↩️ Release Request';
    return type;
  };

  const getDurationText = (request) => {
    if (request.requestType === 'asset_extension') {
      return `Extend for ${request.extendDuration}`;
    }
    if (request.requestType === 'asset_release') {
      return `Return asset${request.reason ? ': ' + request.reason : ''}`;
    }
    return '';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (selectedReject) {
    return (
      <div className="err-modal-overlay">
        <div className="err-modal">
          <div className="err-modal-header">
            <h2>Reject Request</h2>
            <button className="err-close-btn" onClick={() => setSelectedReject(null)}>
              <FaTimes />
            </button>
          </div>

          <div className="err-modal-body">
            <div className="err-info-box">
              <p>
                <strong>Asset:</strong> {selectedReject.assetId?.assetName}
              </p>
              <p>
                <strong>User:</strong> {selectedReject.requestedBy?.name}
              </p>
              <p>
                <strong>Type:</strong> {getRequestTypeLabel(selectedReject.requestType)}
              </p>
            </div>

            <div className="err-form-group">
              <label>Rejection Reason *</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why you're rejecting this request..."
                className="err-textarea"
                rows={4}
              />
            </div>
          </div>

          <div className="err-modal-footer">
            <button 
              className="err-btn err-btn-secondary" 
              onClick={() => setSelectedReject(null)}
            >
              Cancel
            </button>
            <button 
              className="err-btn err-btn-danger"
              onClick={handleRejectSubmit}
              disabled={rejecting || !rejectReason.trim()}
            >
              {rejecting ? <FaSpinner className="spin" /> : <FaTimes />}
              Reject Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="err-page">
      <AssistantSidebar user={user} />
      <div className="err-container">
        <div className="err-header">
        <div className="err-title">
          <FaClock className="err-icon" />
          <div>
            <h2>Extension & Release Requests</h2>
            <p>Approve or reject asset extension and release requests from users</p>
          </div>
        </div>
        {onClose && (
          <button className="err-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        )}
      </div>

      {loading ? (
        <div className="err-loading">
          <FaSpinner className="spin" />
          <p>Loading requests...</p>
        </div>
      ) : error ? (
        <div className="err-error">
          <p>{error}</p>
          <button className="err-btn err-btn-primary" onClick={fetchRequests}>
            Retry
          </button>
        </div>
      ) : requests.length === 0 ? (
        <div className="err-empty">
          <FaClock />
          <p>No extension or release requests at the moment</p>
          <small>Requests will appear here when users request extensions or asset returns</small>
        </div>
      ) : (
        <div className="err-requests-list">
          {requests.map((request) => (
            <div key={request._id} className="err-request-card">
              <div className="err-card-type">
                {getRequestTypeLabel(request.requestType)}
              </div>

              <div className="err-card-content">
                <div className="err-card-top">
                  <h3>{request.assetId?.assetName || 'Unknown Asset'}</h3>
                  <span className={`err-status err-status-${request.status}`}>
                    {request.status}
                  </span>
                </div>

                <div className="err-card-details">
                  <div className="err-detail">
                    <label>Requested By</label>
                    <p>{request.requestedBy?.name}</p>
                  </div>

                  <div className="err-detail">
                    <label>Request Details</label>
                    <p className="err-detail-text">{getDurationText(request)}</p>
                  </div>

                  <div className="err-detail">
                    <label>Date Requested</label>
                    <p>{formatDate(request.createdAt)}</p>
                  </div>
                </div>

                <div className="err-card-actions">
                  <button 
                    className="err-btn err-btn-approve"
                    onClick={() => handleApprove(request._id)}
                    disabled={approving === request._id || request.status !== 'pending'}
                  >
                    {approving === request._id ? (
                      <FaSpinner className="spin" />
                    ) : (
                      <FaCheck />
                    )}
                    {approving === request._id ? 'Approving...' : 'Approve'}
                  </button>

                  <button 
                    className="err-btn err-btn-reject"
                    onClick={() => handleRejectClick(request)}
                    disabled={request.status !== 'pending'}
                  >
                    <FaTimes />
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default ExtensionReleaseRequests;
